import { FormEvent, useState } from "react";
import { Button, Field, Modal, PasswordInput } from "../../ui";
import { useNextT } from "../../i18n";
import { isApiError } from "../../data/errors";
import { useSavePassword, useSendPasswordLink } from "../pageApi";
import { useSavedToast } from "./useSaved";
import s from "../settings.module.scss";

/** Until the page has answered: the server's rule today. The page's `password_min_length` always wins (M3.1: 10 at release). */
export const DEFAULT_PASSWORD_MIN = 8;

/** Unicode isolates around an address inside a sentence: the same job as <bdi>. */
const isolate = (text: string) => `⁨${text}⁩`;

/**
 * Change password (M3.1–M3.4): two fields, the current one and the new one; a person who never set a password sees only the new
 * one. One rule, in the same words everywhere ("At least N characters", N from the server). An eye button on each field. Under
 * the current password, "Forgot it? Send me a link" sends a reset link to the person's own address.
 */
export const PasswordDialog = ({ open, onClose, min, hasPassword = true, email }: { open: boolean; onClose: () => void; min?: number; hasPassword?: boolean; email?: string }) => {
  const MIN = min ?? DEFAULT_PASSWORD_MIN;
  const { t } = useNextT();
  const save = useSavePassword();
  const link = useSendPasswordLink();
  const saved = useSavedToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState<{ current?: string; next?: string; general?: string }>({});
  const [linkState, setLinkState] = useState<"idle" | "sent" | "failed">("idle");
  const rule = t("settingsPage.password.hint", { min: MIN });
  const eye = { showLabel: t("settingsPage.password.show"), hideLabel: t("settingsPage.password.hide") };

  const reset = () => { setCurrent(""); setNext(""); setError({}); setLinkState("idle"); };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const bad: typeof error = {};
    if (hasPassword && !current) bad.current = t("settingsPage.password.current_missing");
    if (next.length < MIN) bad.next = rule;
    setError(bad);
    if (bad.current || bad.next) return;
    save.mutate({ password: next, password_confirmation: next, ...(hasPassword ? { current_password: current } : {}) }, {
      onSuccess: () => { saved(); reset(); onClose(); },
      onError: (err) => {
        const f = isApiError(err) ? err.fieldErrors : {};
        setError({ current: f.current_password ? t("settingsPage.password.wrong_current") : undefined, next: f.password ? rule : undefined, general: f.current_password || f.password ? undefined : t("settingsPage.save_failed") });
      },
    });
  };
  const sendLink = () => link.mutate(undefined, { onSuccess: () => setLinkState("sent"), onError: () => setLinkState("failed") });

  return (
    <Modal open={open} title={t("settingsPage.password.title")} onClose={() => { reset(); onClose(); }} onExited={reset}
      actions={(
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>{t("ui.cancel")}</Button>
          <Button variant="primary" type="submit" form="settings-password" loading={save.isPending}>{t("settingsPage.password.save")}</Button>
        </>
      )}>
      <form id="settings-password" className={s.group} onSubmit={submit} noValidate>
        {hasPassword && (
          <div className={s.group}>
            <Field label={t("settingsPage.password.current")} error={error.current}>
              {(f) => <PasswordInput id={f.id} {...eye} aria-describedby={f.describedBy} invalid={f.invalid} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}
            </Field>
            <div>
              <Button variant="ghost" onClick={sendLink} loading={link.isPending} disabled={linkState === "sent"}>{t("settingsPage.password.forgot")}</Button>
              {linkState !== "idle" && (
                <p role="status" className={s.hint}>
                  {linkState === "sent" ? t("settingsPage.password.forgot_sent", { email: isolate(email ?? "") }) : t("settingsPage.password.forgot_failed")}
                </p>
              )}
            </div>
          </div>
        )}
        {/* The rule shows once: as the hint, or (red) as the error, never both. */}
        <Field label={t("settingsPage.password.new")} hint={error.next ? undefined : rule} error={error.next}>
          {(f) => <PasswordInput id={f.id} {...eye} aria-describedby={f.describedBy} invalid={f.invalid} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}
        </Field>
        {error.general && <p role="alert" className={s.hint}>{error.general}</p>}
      </form>
    </Modal>
  );
};
